import { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Badge,
  Card,
  CardBody,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import classnames from "classnames"
import { useVehicleImporter } from "hooks/useVehicleImporter"
import ThemePlaceholder from "components/Common/ThemePlaceholder"

// Import sub-components
import ImporterLogoSection from "./components/ImporterLogoSection"
import ImporterInfoCard from "./components/ImporterInfoCard"
import AuthorizedPersonsCard from "./components/AuthorizedPersonsCard"
import AddressCard from "./components/AddressCard"
import AdditionalInfoCard from "./components/AdditionalInfoCard"
import { ACCESS } from "constants/accessPermissions"
import { CanView } from "components/Common/CanView"
import ImporterStockContainer from "./tabs/ImporterStockContainer"

const ImporterView = () => {
  const { identifier: importerId } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useVehicleImporter(importerId)
  const importer = data?.data || {}
  const [activeTab, setActiveTab] = useState("vehicles")

  document.title = `Importer${importer?.name ? ` | ${importer.name}` : ""}`

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const addNewImporter = () => {
    navigate(`/importer/add?ref=self`)
  }

  const editImporter = () => {
    navigate(`/importer/${importerId}/edit?ref=self`)
  }

  // Calculate statistics for tab badges
  const tabStats = useMemo(
    () => ({
      documents: importer?.import_documents?.length || 0,
      vehicles: importer?.total_stocks || 0,
      shipments: importer?.shipments?.length || 0,
      receipts: importer?.money_receipts?.length || 0,
    }),
    [importer]
  )

  // Check if importer has any data
  const hasImporterData = useMemo(() => {
    if (isLoading) return false
    return Object.keys(importer).length > 0
  }, [importer, isLoading])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Importer"
          breadcrumbItem={"Details"}
          loading={isLoading}
        />

        {/* Header */}
        <Row className="mb-4">
          <Col lg="8">
            {isLoading ? (
              <div className="d-flex align-items-center">
                <ThemePlaceholder
                  style={{ width: 200, height: 32, borderRadius: 4 }}
                  className="me-3"
                />
                <ThemePlaceholder
                  style={{ width: 100, height: 24, borderRadius: 4 }}
                />
              </div>
            ) : hasImporterData ? (
              <div className="d-flex align-items-center mb-3">
                <h4 className="mb-0 me-3">
                  {importer.name || "Unnamed Importer"}
                </h4>
                {importer?.slug && (
                  <Badge color="light" className="border">
                    {importer.slug}
                  </Badge>
                )}
              </div>
            ) : null}
          </Col>
          <Col lg="4" className="text-end">
            <CanView access={[ACCESS.VEHICLE_IMPORTER.CREATE_VEHICLE_IMPORTER]}>
              <Button color="primary" onClick={addNewImporter} className="me-2">
                <i className="fas fa-plus me-1"></i>
                Add New Importer
              </Button>
            </CanView>
            <CanView access={[ACCESS.VEHICLE_IMPORTER.EDIT_VEHICLE_IMPORTER]}>
              {hasImporterData && (
                <Button
                  color="secondary"
                  outline
                  onClick={() => editImporter()}
                >
                  <i className="fas fa-edit me-1"></i>
                  Edit Importer
                </Button>
              )}
            </CanView>
          </Col>
        </Row>

        {!isLoading && !hasImporterData ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="bx bx-building display-4 text-muted mb-3"></i>
              <h5>Importer not found</h5>
              <p className="text-muted mb-4">
                The importer you are looking for does not exist or has been
                deleted.
              </p>
              <Button color="primary" onClick={() => navigate("/importers")}>
                Back to Importers
              </Button>
            </CardBody>
          </Card>
        ) : (
          <Row>
            {/* Left Column - All importer info */}
            <Col lg="4" className="mb-4">
              {/* Logo Section */}
              <ImporterLogoSection
                logo={importer?.logo}
                name={importer?.name}
                loading={isLoading}
              />

              {/* General Information */}
              <ImporterInfoCard importer={importer} loading={isLoading} />

              {/* Authorized Persons */}
              <AuthorizedPersonsCard
                persons={importer?.authorized_persons}
                loading={isLoading}
              />

              {/* Addresses */}
              <AddressCard addresses={importer?.address} loading={isLoading} />

              {/* Additional Information */}
              <AdditionalInfoCard importer={importer} loading={isLoading} />
            </Col>

            {/* Right Column - Tabs for related data */}
            <Col lg="8">
              <Nav pills className="nav-pills-custom mb-3">
                {[
                  {
                    id: "vehicles",
                    label: `Vehicles`,
                    icon: "bx bx-car",
                    count: tabStats.vehicles,
                  },
                ]?.map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: activeTab === tab.id })}
                      onClick={() => toggleTab(tab.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {tab.icon && <i className={`${tab.icon} me-2`}></i>}
                      {tab.label}
                      {tab.count !== null && tab.count > 0 && (
                        <Badge color="danger" pill className="ms-2">
                          {tab.count}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab}>
                {/* You can implement actual tab content here later */}
                <TabPane tabId="vehicles">
                  <ImporterStockContainer importerId={importerId} />
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  )
}

export default ImporterView
